import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys = 'governanceManagerScreen';

type UIEventKeys =
  | 'clickedGovernanceManagerLearnMoreLink'
  | 'clickedGovernanceManagerComplianceLink'
  | 'clickedGovernanceManagerAppDetailsExpandToggle'
  | 'clickedGovernanceManagerActionReasonLink'
  | 'governanceManagerTotalSitesModalOpened'
  | 'governanceManagerActionsRemainingModalOpened'
  | 'governanceManagerActionsRemainingActionClicked'
  | 'governanceManagerAuditLogModalOpened'
  | 'governanceManagerAuditLogViewMoreClicked'
  | 'governanceManagerSettingsModalOpened'
  | 'governanceManagerSettingsScopeTabViewed'
  | 'governanceManagerSettingsExportTabViewed'
  | 'governanceManagerOnboardingStarted'
  | 'governanceManagerOnboardingWizardActivated'
  | 'governanceManagerOnboardingWizardClosed'
  | 'governanceManagerExportInitiated'
  | 'governanceManagerExportFailed';

type TrackEventKeys = 'governanceManagerScopeChanged';

type OperationalEventKeys = string;

export const governanceManagerScreen = 'governanceManagerScreen';

export const governanceManagerEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, OperationalEventKeys> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/ETF/pages/5480056418/Compliance+Manager+M1+Analytics+Spec',
  screen: {
    governanceManagerScreen: (attributes: { isEmpty?: boolean }): ScreenEvent => ({
      data: {
        name: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85623',
    }),
  },
  ui: {
    clickedGovernanceManagerLearnMoreLink: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'governanceManagerLearnMoreLink',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85624',
    }),
    clickedGovernanceManagerComplianceLink: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'governanceManagerComplianceLink',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85625',
    }),
    clickedGovernanceManagerAppDetailsExpandToggle: (attributes: { isExpanded: boolean; workspaceAri: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerComplianceAppDetailsExpandToggle',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85625',
    }),
    clickedGovernanceManagerActionReasonLink: (attributes: { frameworkType: string; href: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'governanceManagerActionReasonLink',
        source: governanceManagerScreen,
        attributes,
      },
    }),
    governanceManagerTotalSitesModalOpened: (attributes: { standardIdentifier: string; totalSitesCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Modal,
        actionSubjectId: 'governanceManagerTotalSitesModal',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99214',
    }),
    governanceManagerActionsRemainingModalOpened: (attributes: { standardTitle: string; totalActionsCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Modal,
        actionSubjectId: 'governanceManagerActionsRemainingModal',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99217',
    }),
    governanceManagerActionsRemainingActionClicked: (attributes: { configurationId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerActionsRemainingAction',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99216',
    }),
    governanceManagerAuditLogModalOpened: (attributes: { standardTitle: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Modal,
        actionSubjectId: 'governanceManagerAuditLogModal',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99218',
    }),
    governanceManagerAuditLogViewMoreClicked: (attributes: { standardTitle: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerAuditLogViewMore',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99219',
    }),
    governanceManagerSettingsModalOpened: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Modal,
        actionSubjectId: 'governanceManagerSettingsModal',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99220',
    }),
    governanceManagerSettingsScopeTabViewed: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Tab,
        actionSubjectId: 'governanceManagerSettingsScopeTab',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99221',
    }),
    governanceManagerSettingsExportTabViewed: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Tab,
        actionSubjectId: 'governanceManagerSettingsExportTab',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99222',
    }),
    governanceManagerOnboardingStarted: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerOnboardingStart',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99223',
    }),
    governanceManagerOnboardingWizardActivated: (attributes: { mappingCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerOnboardingWizardActivate',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99224',
    }),
    governanceManagerOnboardingWizardClosed: (attributes: { completedStep: number }): UIEvent => ({
      data: {
        action: Action.Closed,
        actionSubject: ActionSubject.Modal,
        actionSubjectId: 'governanceManagerOnboardingWizard',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99225',
    }),
    governanceManagerExportInitiated: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerExport',
        source: governanceManagerScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99226',
    }),
    governanceManagerExportFailed: (attributes: { errorMessage: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'governanceManagerExportFailed',
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99227',
    }),
  },
  track: {
    governanceManagerScopeChanged: (attributes: {
      scope: Array<{ domain: string; [standardId: string]: boolean | string }>;
      source: 'settings' | 'onboarding';
    }): TrackEvent => ({
      data: {
        action: TrackAction.Changed,
        actionSubject: TrackActionSubject.GovernanceManagerScope,
        source: governanceManagerScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99228',
    }),
  },
  operational: {},
};
